import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

export interface LoginBanner {
  /** Tall artwork for the desktop side column. Null until one is uploaded. */
  image: string | null;
  /** Short artwork for phones, where the tall one cannot fit. */
  mobileImage: string | null;
  /** Caption for the artwork. Empty string when the admin left it blank. */
  title: string;
  /** Where the artwork links to, or null when it is not clickable. */
  link: string | null;
}

/** Shape of `GET /api/v1/dev-panel-banner`. */
interface DevPanelBannerResponse {
  data?: {
    title?: string;
    url?: string;
    images?: {
      desktop?: string | null;
      mobile?: string | null;
    };
  };
}

/**
 * The banner is administered in sibche, which is a different service from the
 * publisher backend `apiClient` points at — hence a bare axios call rather than
 * that client.
 */
const SIBCHE_API_URL =
  import.meta.env.VITE_SIBCHE_API_URL ?? 'https://api.sibche.com';

// No `/api/v1` prefix: that host is already the v1 api root, and asking for
// `/api/v1/dev-panel-banner` there returns a 404.
const ENDPOINT = `${SIBCHE_API_URL.replace(/\/$/, '')}/dev-panel-banner`;

/**
 * Promotional artwork for the auth screens, managed from the sibche admin panel.
 *
 * Only the urls are fetched: the images themselves sit at a fixed key on
 * storage, so every response carries the same address with a `?v=` cache
 * buster that only changes when a new file is uploaded. Either image may be
 * null when nothing has been uploaded yet, so callers should treat a missing
 * banner as the normal case.
 */
const useLoginBanner = () =>
  useQuery<LoginBanner | null>({
    queryKey: ['dev-panel-banner'],
    queryFn: async () => {
      const { data } = await axios.get<DevPanelBannerResponse>(ENDPOINT);
      const banner = data?.data;
      if (!banner) return null;

      return {
        image: banner.images?.desktop ?? null,
        mobileImage: banner.images?.mobile ?? null,
        title: banner.title ?? '',
        // Empty string means "not clickable", so it must not become a link.
        link: banner.url || null,
      };
    },
    staleTime: 5 * 60 * 1000,
    // The login form must never be blocked by artwork: a failed or slow
    // request should just leave the banner out.
    retry: false,
  });

export default useLoginBanner;
